import React, { createContext, useContext, useState, useCallback } from "react";
import cssCheatSheet from "../data/cssCheatSheet";
import gitCheatsheet from "../data/gitCheatsheet";
import phpCheatSheet from "../data/phpCheatSheet";
import sqlCheatSheet from "../data/sqlCheatSheet";
import uxuiCheatSheet from "../data/uxuiCheatSheet";
import javascriptCheatSheet from "../data/javascriptCheatSheet";
import pythonCheatsheet from "../data/pythonCheatsheet";
import reactCheatsheet from "../data/reactCheatsheet";
import typescriptCheatsheet from "../data/typescriptCheatsheet";
import nodejsCheatsheet from "../data/nodejsCheatsheet";
import expressCheatsheet from "../data/expressCheatsheet";
import mongodbCheatsheet from "../data/mongodbCheatsheet";
import html5CheatSheet from "../data/html5CheatSheet";
import dockerCheatsheet from "../data/dockerCheatsheet";
import excelCheatSheet from "../data/excelCheatSheet";
import tableauCheatSheet from "../data/tableauCheatSheet";
import powerBICheatSheet from "../data/powerBICheatSheet";

// Create the context first
const SearchContext = createContext(null);

// Helper function to create section IDs - must match CheatSheetContent.js exactly
const createSectionId = (text) => {
  if (!text) return "";

  // First convert to lowercase and replace special characters
  let id = text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-") // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens

  // Add 'section-' prefix if the ID starts with a number
  if (/^\d/.test(id)) {
    id = `section-${id}`;
  }

  // Handle common special cases
  const specialCases = {
    "error-handling": "error-handling",
    "object-oriented-programming": "object-oriented-programming",
    "database-operations": "database-operations",
    "security-best-practices": "security-best-practices",
    "meta-seo": "meta-seo",
    "links-lists": "links-lists",
    "audio-video": "audio-video",
    "form-structure": "form-structure",
    "form-elements": "form-elements",
    "basic-table": "basic-table",
    "table-features": "table-features",
    "layout-elements": "layout-elements",
    "text-semantics": "text-semantics",
    "meta-tags": "meta-tags",
    "link-tags": "link-tags",
    "getting-started": "getting-started",
    "basic-syntax": "basic-syntax",
    "data-types": "data-types",
    "control-structures": "control-structures",
    functions: "functions",
    arrays: "arrays",
    strings: "strings",
    objects: "objects",
    classes: "classes",
    modules: "modules",
    "async-programming": "async-programming",
    "error-handling": "error-handling",
    "file-operations": "file-operations",
    "database-connections": "database-connections",
    security: "security",
    testing: "testing",
    deployment: "deployment",
  };

  // Return special case if it exists, otherwise return processed ID
  return specialCases[id] || id;
};

// Helper function to format cheatsheet data for search
const formatCheatsheetData = (cheatsheet) => {
  const results = [];
  const category = cheatsheet.title.toLowerCase().replace(/\s+/g, "-");

  // Special handling for different cheatsheets
  const getBasePath = (cat) => {
    switch (cat) {
      case "html5-cheatsheet":
        return "/cheatsheet/html";
      case "php":
        return "/cheatsheet/php";
      case "css3-cheatsheet":
        return "/cheatsheet/css";
      case "git-cheatsheet":
        return "/cheatsheet/git";
      case "sql-cheatsheet":
        return "/cheatsheet/sql";
      case "ux/ui-design-cheatsheet":
        return "/cheatsheet/ux-ui";
      case "javascript-cheatsheet":
        return "/cheatsheet/javascript";
      case "python-cheatsheet":
        return "/cheatsheet/python";
      case "react-cheatsheet":
        return "/cheatsheet/react";
      case "typescript-cheatsheet":
        return "/cheatsheet/typescript";
      case "node.js-cheatsheet":
        return "/cheatsheet/nodejs";
      case "express.js-cheatsheet":
        return "/cheatsheet/express";
      case "mongodb-cheatsheet":
        return "/cheatsheet/mongodb";
      case "docker-cheatsheet":
        return "/cheatsheet/docker";
      case "excel-cheatsheet":
        return "/cheatsheet/excel";
      case "tableau-cheatsheet":
        return "/cheatsheet/tableau";
      case "power-bi-cheatsheet":
        return "/cheatsheet/powerbi";
      default:
        return `/cheatsheet/${cat}`;
    }
  };

  const basePath = getBasePath(category);

  cheatsheet.sections.forEach((section) => {
    const sectionId = createSectionId(section.title);
    const items = section.items || section.content || [];

    // For sections, use the first item's content as a preview if no description
    const sectionContent =
      section.description ||
      section.explanation ||
      (items[0] &&
        (items[0].explanation || items[0].description || items[0].content)) ||
      `${section.title} section of the ${category} cheatsheet`;

    // Add section result if it matches
    results.push({
      type: "section",
      title: section.title,
      category,
      content: sectionContent,
      link: `${basePath}#${sectionId}`,
      sectionId,
      section: section.title,
      tags: [category, "section"],
      id: `${category}-section-${sectionId}`,
    });

    items.forEach((item) => {
      const itemId = createSectionId(item.title);
      const itemContent =
        item.explanation || item.description || item.content || "";

      // Add item result with section and item IDs
      results.push({
        type: "item",
        title: item.title,
        category,
        content: itemContent,
        code: item.code || "",
        link: `${basePath}#${sectionId}-${itemId}`,
        itemId,
        section: section.title,
        subsection: item.title,
        tags: [category, section.title],
        id: `${category}-item-${itemId}`,
        note: item.note || "",
      });

      // Add examples if they exist
      if (item.examples) {
        item.examples.forEach((example, index) => {
          const exampleContent =
            example.explanation ||
            example.description ||
            example.content ||
            itemContent;
          results.push({
            type: "example",
            title: example.title || item.title,
            category,
            content: exampleContent,
            code: example.code || "",
            link: `${basePath}#${sectionId}-${itemId}-example-${index}`,
            section: section.title,
            subsection: item.title,
            tags: [category, "example"],
            id: `${category}-example-${itemId}-${index}`,
          });
        });
      }
    });
  });

  // Log first few results for debugging
  console.log(
    "Detailed search results:",
    JSON.stringify(
      results.slice(0, 3).map((r) => ({
        id: r.id,
        title: r.title,
        content: r.content?.substring(0, 50) + "...",
        tags: r.tags,
        type: r.type,
        category: r.category,
        section: r.section,
        subsection: r.subsection,
        link: r.link,
      })),
      null,
      2
    )
  );

  return results;
};

// Combine all cheatsheet data and format it for search
const allCheatsheets = [
  ...formatCheatsheetData(cssCheatSheet),
  ...formatCheatsheetData(gitCheatsheet),
  ...formatCheatsheetData(phpCheatSheet),
  ...formatCheatsheetData(sqlCheatSheet),
  ...formatCheatsheetData(uxuiCheatSheet),
  ...formatCheatsheetData(javascriptCheatSheet),
  ...formatCheatsheetData(pythonCheatsheet),
  ...formatCheatsheetData(reactCheatsheet),
  ...formatCheatsheetData(typescriptCheatsheet),
  ...formatCheatsheetData(nodejsCheatsheet),
  ...formatCheatsheetData(expressCheatsheet),
  ...formatCheatsheetData(mongodbCheatsheet),
  ...formatCheatsheetData(html5CheatSheet),
  ...formatCheatsheetData(dockerCheatsheet),
  ...formatCheatsheetData(excelCheatSheet),
  ...formatCheatsheetData(tableauCheatSheet),
  ...formatCheatsheetData(powerBICheatSheet),
];

// Helper function to calculate result relevance
const calculateRelevance = (item, searchTerms) => {
  let score = 0;
  const termScores = searchTerms.map((term) => {
    let termScore = 0;

    // Exact matches have highest priority
    if (item.title?.toLowerCase() === term) termScore += 10;
    if (item.section?.toLowerCase() === term) termScore += 8;
    if (item.subsection?.toLowerCase() === term) termScore += 7;

    // Partial matches have lower priority
    if (item.title?.toLowerCase().includes(term)) termScore += 5;
    if (item.section?.toLowerCase().includes(term)) termScore += 4;
    if (item.subsection?.toLowerCase().includes(term)) termScore += 3;
    if (item.category?.toLowerCase().includes(term)) termScore += 2;

    // Content and tag matches
    if (item.content?.toLowerCase().includes(term)) termScore += 1;
    item.tags?.forEach((tag) => {
      if (tag.toLowerCase().includes(term)) termScore += 1;
    });

    return termScore;
  });

  // Average the scores across all search terms
  score =
    termScores.reduce((sum, score) => sum + score, 0) / searchTerms.length;

  // Boost certain result types
  if (item.type === "section") score *= 1.2;
  if (item.type === "example") score *= 0.8;

  return score;
};

// Define the hook before the provider
export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error("useSearch must be used within a SearchProvider");
  }
  return context;
};

// Export the provider component
export const SearchProvider = ({ children }) => {
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const search = useCallback(async (query) => {
    if (!query?.trim()) {
      setSearchResults([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await new Promise((resolve) => setTimeout(resolve, 300));
      const searchTerms = query.toLowerCase().trim().split(/\s+/);

      console.log("Searching for terms:", searchTerms);

      const results = allCheatsheets
        .filter((item) => {
          // Ensure all required fields exist
          if (!item || !item.title || !item.content) return false;

          return searchTerms.some((term) => {
            const fields = {
              title: item.title?.toLowerCase() || "",
              content: item.content?.toLowerCase() || "",
              tags: (item.tags || []).map((t) => t.toLowerCase()),
              category: item.category?.toLowerCase() || "",
              section: item.section?.toLowerCase() || "",
              subsection: item.subsection?.toLowerCase() || "",
            };

            return Object.values(fields).some((value) =>
              Array.isArray(value)
                ? value.some((v) => v.includes(term))
                : value.includes(term)
            );
          });
        })
        .map((item) => ({
          ...item,
          content: item.content || "",
          tags: item.tags || [],
          category: item.category || "",
          section: item.section || "",
          subsection: item.subsection || "",
          title: item.title || "",
          id:
            item.id ||
            `${item.category || "unknown"}-${item.title || "untitled"}`,
          link: item.link || "",
          note: item.note || "",
          type: item.type || "unknown",
        }))
        .sort(
          (a, b) =>
            calculateRelevance(b, searchTerms) -
            calculateRelevance(a, searchTerms)
        );

      // Log detailed info about first few results
      console.log(
        "Detailed search results:",
        JSON.stringify(
          results.slice(0, 3).map((r) => ({
            id: r.id,
            title: r.title,
            content: r.content?.substring(0, 50) + "...",
            tags: r.tags,
            type: r.type,
            category: r.category,
            section: r.section,
            subsection: r.subsection,
            link: r.link,
            relevance: calculateRelevance(r, searchTerms),
          })),
          null,
          2
        )
      );

      setSearchResults(results);
    } catch (err) {
      setError("Search failed. Please try again.");
      console.error("Search error:", err);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Google search fallback
  const searchGoogle = useCallback((query) => {
    const googleUrl = `https://www.google.com/search?q=${encodeURIComponent(
      query
    )}+site:developer.mozilla.org+OR+site:stackoverflow.com`;
    window.open(googleUrl, "_blank");
  }, []);

  const value = {
    searchResults,
    isLoading,
    error,
    search,
    searchGoogle,
  };

  return (
    <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
  );
};
